<template>
  <v-stepper-vertical-item :value="4" :elevation="0" :class="{ 'step-completed': currentStep > 4 }">
    <template #title>
      <div class="subtitle1">{{ $t("modals.allowCloudService.step4.title") }}</div>
    </template>
    <template #icon>
      <v-icon v-if="currentStep > 4" icon="$check" />
      <span v-else>5</span>
    </template>
    <div>
      <div class="body2">{{ $t("modals.allowCloudService.step4.subtitle") }}</div>

      <assign-users
        @model-change="onAssignUserChange"
        :groups="modelValue.groups"
        :users="modelValue.users"
        :labels="modelValue.labels"
        :hideGroups="isOtherProvider"
        :service="modelValue.selectedService"
        :user-type="modelValue.userType"
      ></assign-users>

      <div class="mt-5 d-flex">
        <v-btn color="primary" rounded :loading="loading" min-width="120" @click="moveToNextStep">
          {{ $t("modals.allowCloudService.step4.actionBtn") }}
        </v-btn>
        <v-btn class="ml-2" variant="text" color="primary" @click="goBack">
          {{ $t("general.back") }}
        </v-btn>
      </div>
    </div>
  </v-stepper-vertical-item>
</template>

<script lang="ts">
import { type PropType, computed, defineComponent, ref, watch } from "vue";
import { Service } from "@/constants/cloud-apps.ts";
import {
  SatConfigurationStatus,
  SatServiceProvider,
  SatServiceProviderToServiceMap,
} from "@/constants/security-awareness.ts";
import { useSecurityAwarenessModule } from "@/_store/security-awareness/security-awareness.module.ts";
import AssignUsers, {
  type AssignUsersForm,
} from "@/components/modals/security-awareness/AssignUsers.vue";
import type { SatConnectionConfigForm } from "@/components/modals/security-awareness/allow-security-awareness.types.ts";

export default defineComponent({
  components: { AssignUsers },
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Object as PropType<SatConnectionConfigForm>,
      required: true,
    },
  },
  emits: ["update:modelValue", "next", "back"],
  setup(props, { emit }) {
    const securityAwarenessStore = useSecurityAwarenessModule();
    const loading = ref(false);

    const formValue = computed(() => props.modelValue);

    const updateLocalValue = (partial: Partial<SatConnectionConfigForm>) => {
      emit("update:modelValue", { ...props.modelValue, ...partial });
    };

    watch(
      () => formValue.value.userType,
      () => {
        updateLocalValue({
          groups: [],
          users: [],
          labels: [],
        });
      }
    );

    const isOtherProvider = computed(() => {
      return formValue.value.selectedService === "other";
    });

    const satConfigurationProvider = computed(
      () =>
        SatServiceProviderToServiceMap[formValue.value.selectedService as Service] ??
        SatServiceProvider.CUSTOM
    );

    const onAssignUserChange = (newValue: AssignUsersForm) => {
      updateLocalValue({
        ...newValue,
      });
    };

    const moveToNextStep = async () => {
      loading.value = true;
      const { users, groups, labels } = formValue.value;
      // If user selects "All users" option we send just the status
      const config =
        users.length || groups.length || labels.length
          ? {
              status: SatConfigurationStatus.PROTECTED_USERS_ASSIGNED,
              protectedUserEmails: users.map((user) => user.email),
              protectedGroups: groups.map((group) => ({ id: group.groupId })),
              protectedLabels: labels,
            }
          : {
              status: SatConfigurationStatus.PROTECTED_USERS_ASSIGNED,
            };

      await securityAwarenessStore.updateSatConfig(config, satConfigurationProvider.value);
      loading.value = false;
      emit("next");
    };

    const goBack = () => {
      emit("back");
    };

    return {
      isOtherProvider,
      onAssignUserChange,
      moveToNextStep,
      loading,
      goBack,
    };
  },
});
</script>
